import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getOrderStatus } from "../api/api";
import "../styles/orderTracking.css";

const OrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const hasRedirectedRef = React.useRef(false);

  useEffect(() => {
    fetchOrderStatus();

    // Poll for order updates every 5 seconds
    const interval = setInterval(fetchOrderStatus, 5000);

    return () => clearInterval(interval);
  }, [orderId]);

  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  useEffect(() => {
    if (order?.status !== "completed") return;
    if (hasRedirectedRef.current) return;

    hasRedirectedRef.current = true;

    localStorage.removeItem("activeOrder");

    setTimeout(() => {
      setShowThankYou(true);
    }, 1500);
  }, [order]);

  useEffect(() => {
    if (showThankYou) {
      const timer = setTimeout(() => {
        navigate("/");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showThankYou]);

  const fetchOrderStatus = async () => {
    try {
      const data = await getOrderStatus(orderId);
      setOrder(data);

      // Calculate estimated time based on status
      if (data.status === "preparing") {
        setTimeRemaining(data.remaining_time);
      }
    } catch (error) {
      console.error("Error fetching order:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return "--:--";

    const totalSeconds = Math.floor(seconds);

    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;

    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const getOrderStatusDetails = () => {
    if (!order) return { text: "Processing", icon: "⏳", progress: 10 };

    const statusMap = {
      pending: {
        text: "Order Received",
        icon: "📋",
        progress: 20,
        color: "#ffa502",
      },
      preparing: {
        text: "Chef is Preparing",
        icon: "👨‍🍳",
        progress: 50,
        color: "#0baf9a",
      },
      ready: {
        text: "Ready for Pickup",
        icon: "✅",
        progress: 80,
        color: "#4cd964",
      },
      completed: {
        text: "Order Completed",
        icon: "🎉",
        progress: 100,
        color: "#0baf9a",
      },
      cancelled: {
        text: "Order Cancelled",
        icon: "❌",
        progress: 0,
        color: "#ff6b6b",
      },
    };

    return statusMap[order.status] || statusMap["pending"];
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading order details...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="error-container">
        <h2>Order not found</h2>
        <button onClick={() => navigate("/")}>Go Home</button>
      </div>
    );
  }

  const status = getOrderStatusDetails();

  if (showThankYou) {
    return (
      <div className="thank-you-screen">
        <div className="thank-you-card">
          <div className="success-animation">🎉</div>

          <h1 className="thank-title">Order Completed!</h1>

          <p className="thank-subtitle">
            Your order has been successfully completed 🍽️
          </p>

          <div className="thank-box">
            <p>✨ Your food has been served successfully</p>
            <p>💖 Hope you enjoyed your meal</p>
          </div>

          <button className="thank-btn" onClick={() => navigate("/")}>
            Back to menu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-tracking">
      <div className="tracking-header">
        <button onClick={() => navigate("/")} className="home-btn">
          ← Back to Menu
        </button>
        <h2>Order #{order.order_id || orderId}</h2>
        <p>{order.table_name}</p>
      </div>

      <div
        className="order-status-card"
        style={{ borderTopColor: status.color }}>
        <div className="status-icon">{status.icon}</div>
        <h3>{status.text}</h3>

        {timeRemaining > 0 && status.progress === 50 && (
          <div className="timer">
            <span className="timer-icon">⏱️</span>
            <span className="timer-text">
              Time Remaining: {formatTime(timeRemaining)}
            </span>
          </div>
        )}

        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{
              width: `${status.progress}%`,
              backgroundColor: status.color,
            }}></div>
        </div>
      </div>

      <div className="order-items">
        <h3>Your Order</h3>
        {order.items &&
          order.items.map((item, index) => (
            <div className="order-item" key={index}>
              <div className="item-info">
                <span className="item-qty">{item.quantity}×</span>
                <span className="item-name">{item.name || item.item_name}</span>
                {item.variant && (
                  <span className="item-variant">({item.variant})</span>
                )}
              </div>
              <span className="item-price">
                ₹{item.total || item.price * item.quantity}
              </span>
            </div>
          ))}
      </div>

      <div className="order-total">
        <div className="total-row">
          <span>Subtotal:</span>
          <span>₹{order.subtotal}</span>
        </div>
        {/* <div className="total-row">
          <span>Delivery:</span>
          <span>
            {order.delivery_charge === 0 ? "FREE" : `₹${order.delivery_charge}`}
          </span>
        </div> */}
        {order.discount > 0 && (
          <div className="total-row">
            <span>Discount:</span>
            <span>-₹{order.discount}</span>
          </div>
        )}
        <div className="total-row grand-total">
          <span>Total:</span>
          <span>₹{order.total || order.total_amount}</span>
        </div>
      </div>

      {/* {order.status === "ready" && (
        <button
          className="complete-order-btn"
          onClick={() => {
            alert("Enjoy your meal! Thank you for ordering at Table 7.");
            navigate("/");
          }}>
          Complete Order
        </button>
      )} */}

      {order.status === "preparing" && (
        <p className="preparing-message">
          Your order is being prepared by our expert chefs. We'll notify you
          when it's ready!
        </p>
      )}
    </div>
  );
};

export default OrderTracking;
